import CropIcon from '@mui/icons-material/Crop';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import Cropper, { ReactCropperElement } from 'react-cropper';
import { CropData, getIsValidCropObj, ImageData } from '../ImageUpload/ImageData';

// Normalize older react undefined ref type (Cropper | null)
type CropRefType = NonNullable<ReactCropperElement['cropper']> | null;

export type ImageCropHandle = {
  /** Get the cropper instance */
  getCropper(): CropRefType;
  /** Direct get access: ref?.current?.cropper */
  readonly cropper: CropRefType;
};

export type ImageCropRef = React.Ref<ImageCropHandle>;

export type ImageCropProps = {
  imgData: ImageData;
  onImgDataUpdate: (updatedImgData: ImageData) => void;
  sx?: SxProps;
  onCropperReady?: (event: Cropper.ReadyEvent<HTMLImageElement>) => void;
  keepCropMounted?: boolean;
  triggerButtonProps?: {
    label?: string;
    variant?: 'text' | 'outlined' | 'contained';
    size?: 'small' | 'medium' | 'large';
    disabled?: boolean;
    fullWidth?: boolean;
  };
};

export const defaultCrop: Required<CropData> = { left: 0, top: 0, width: 1, height: 1 };

export const aspectRatios = [1 / 1, 2 / 1, 2 / 3, 4 / 3, 8 / 3, 16 / 9, 40 / 21];

export function getAspectRatio(width: number, height: number) {
  const ratio = parseFloat((width / height).toFixed(6));
  for (const aspect of aspectRatios) {
    if (Math.abs(ratio - parseFloat(aspect.toFixed(6))) < 0.01) {
      return aspect;
    }
  }
  return 0;
}

// Convert cropper data to BLOX view_crop
export function convertToViewCrop(
  x: number,
  y: number,
  width: number,
  height: number,
  totalWidth: number,
  totalHeight: number,
) {
  const left = (x / totalWidth).toFixed(2);
  const top = (y / totalHeight).toFixed(2);
  const cropWidth = (width / totalWidth).toFixed(6);
  const cropHeight = (height / totalHeight).toFixed(6);

  return {
    left: parseFloat(left),
    top: parseFloat(top),
    width: parseFloat(cropWidth),
    height: parseFloat(cropHeight),
  };
}

// Convert BLOX view_crop to cropper data
export function convertToCropper(
  viewCrop: CropData,
  totalWidth: number,
  totalHeight: number,
) {
  // guard invalid return default
  const cropValid = getIsValidCropObj(viewCrop);
  if (!cropValid) {
    const fallback = { x: 0, y: 0, width: totalWidth, height: totalHeight };
    return fallback;
  }

  // cropobj is valid, calc cropper values
  const x = viewCrop.left * totalWidth;
  const y = viewCrop.top * totalHeight;
  const cropWidth = viewCrop.width * totalWidth;
  const cropHeight = viewCrop.height * totalHeight;

  // final parsing and rounding
  return {
    x: parseFloat(x.toFixed(2)),
    y: parseFloat(y.toFixed(2)),
    width: parseFloat(cropWidth.toFixed(6)),
    height: parseFloat(cropHeight.toFixed(6)),
  };
}

/**
 * Image Cropper Dialog
 */
export const ImageCrop = memo(
  forwardRef<ImageCropHandle, ImageCropProps>(
    (
      {
        imgData,
        onImgDataUpdate,
        sx,
        triggerButtonProps,
        onCropperReady,
        keepCropMounted = false,
      }: ImageCropProps,
      ref,
    ) => {
      const theme = useTheme();
      const { t } = useTranslation();
      const [open, setOpen] = useState(false);
      const [initialAspectRatio, setInitialAspectRatio] = useState(0);
      const [selectedAspectRatio, setSelectedAspectRatio] = useState(0);
      const [isLoading, setIsLoading] = useState(true);
      const isFullScreen = useMediaQuery(theme.breakpoints.down('sm'));
      const cropperRef = useRef<ReactCropperElement | null>(null);

      // Translation strings
      const strCrop = t('blox.actions.crop');
      const strUpdate = t('blox.actions.update');
      const strCancel = t('blox.actions.cancel');
      const strRemoveCrop = t('blox.actions.remove_crop');
      const strAspectRatio = t('blox.general.aspect_ratio');

      // react-cropper styles (uses cropper.js v1 css)
      const cropperSx = useMemo(() => getCropperStyles(theme), [theme]);

      /**
       * Ability to share the internal cropper instance via fwd ref.
       * Note: the ref will be null if modal/component is not mounted.
       * Can pass keepCropMounted to keep mounted making the
       * ref accessible even when the dialog is closed, which
       * changes when the onCropperReady callback is fired.
       *
       * Generally this is not needed for most use cases.
       * If trying to use this for preview extraction via ref its
       * better to use the standalone "getCroppedViaCanvas" function
       * as it is more accurate / reliable than trying to extract
       * the cropped region via the ref, which can be inaccurate if it
       * was moved via the react-cropper ui but not confirmed yet.
       */
      useImperativeHandle(
        ref,
        () => ({
          getCropper: () => cropperRef.current?.cropper ?? null,
          get cropper() {
            return cropperRef.current?.cropper ?? null;
          },
        }),
        [cropperRef],
      );

      /**
       * Reset loading state on image src url change since
       * react-cropper only emits the ready event on first mount.
       * We track loading state up here, so when image url changes
       * it should run and emit again, handled by handleOnReady cb.
       * Normalize str for effect check (or use getBaseCleanUrl).
       */
      const prevUrlRef = useRef(imgData?.src || '');
      const currPropUrl = imgData?.src || '';
      useEffect(() => {
        if (prevUrlRef.current !== currPropUrl) {
          setIsLoading(true);
          prevUrlRef.current = currPropUrl;
        }
      }, [currPropUrl]);

      /**
       * Current imgdata converted to cropper.js data
       */
      const currentCrop = useMemo(() => {
        return convertToCropper(
          imgData.crop || defaultCrop,
          imgData.width || 1,
          imgData.height || 1,
        );
      }, [imgData.crop, imgData.width, imgData.height]);

      /**
       * Sync the croppers internal state with currentCrop.
       * Updates aspect ratio and crop box position/size.
       *
       * Also if the user moved the crop area but did not
       * confirm (no imgData update), revert the ref state
       * internals back to the "current" form state
       * on cancel or next open.
       */
      useEffect(() => {
        const cropper = cropperRef.current?.cropper;
        // Always update UI state to match imgData
        const aspect = getAspectRatio(currentCrop.width, currentCrop.height);
        setInitialAspectRatio(aspect);
        setSelectedAspectRatio(aspect);
        // Only sync cropper if it's ready
        if (!cropper || isLoading) {
          return;
        }
        // Get cropper's current internal state
        const internalData = cropper.getData();
        // Check if crop dimensions differs from imgData crop
        // Math.abs() for float precision fixes (-0, 0.00)
        const xDiff = Math.abs(internalData.x - currentCrop.x);
        const yDiff = Math.abs(internalData.y - currentCrop.y);
        const widthDiff = Math.abs(internalData.width - currentCrop.width);
        const heightDiff = Math.abs(internalData.height - currentCrop.height);
        const needsSync = xDiff || yDiff || widthDiff || heightDiff;
        // Sync cropper if there are differences
        if (needsSync) {
          cropper.setData(currentCrop);
        }
      }, [currentCrop, isLoading]);

      const handleOnReady = (event: Cropper.ReadyEvent<HTMLImageElement>) => {
        setIsLoading(false);
        if (onCropperReady) {
          onCropperReady(event);
        }
      };

      const handleOpen = () => {
        setOpen(true);
        // reset cropper to current on open if ref attached
        if (cropperRef.current?.cropper) {
          cropperRef.current.cropper.setData(currentCrop);
          const aspect = getAspectRatio(currentCrop.width, currentCrop.height);
          setSelectedAspectRatio(aspect);
          setInitialAspectRatio(aspect);
        }
      };

      const handleClose = () => {
        setOpen(false);
      };

      const handleCancel = () => {
        // reset cropper to current data on cancel
        // in case the user messed with the drag areas
        if (cropperRef.current?.cropper) {
          cropperRef.current.cropper.setData(currentCrop);
          const aspect = getAspectRatio(currentCrop.width, currentCrop.height);
          setSelectedAspectRatio(aspect);
          setInitialAspectRatio(aspect);
        }
        handleClose();
      };

      const handleCrop = () => {
        const newData = getCropData();
        const updatedImageState = newData?.updatedImageState;
        // no new data or ref not ready
        if (!newData || !updatedImageState) {
          handleClose();
          return;
        }
        // send updated image data to parent
        onImgDataUpdate(updatedImageState);
        handleClose();
      };

      const handleSelectChange = (event: SelectChangeEvent<number>) => {
        const cropper = cropperRef.current?.cropper;
        cropper?.setAspectRatio(Number(event.target.value));
        setSelectedAspectRatio(Number(event.target.value) || 0);
      };

      const handleRemoveCrop = () => {
        const cropper = cropperRef.current?.cropper;
        cropper?.reset();
        cropper?.setAspectRatio(0);
        setSelectedAspectRatio(0);
      };

      const getCropData = () => {
        if (cropperRef.current?.cropper) {
          const croppedCanvas = cropperRef.current?.cropper.getCroppedCanvas();
          const croppedDataURL = croppedCanvas?.toDataURL();

          if (croppedDataURL) {
            const cropData = cropperRef.current?.cropper.getData();

            /**
             * ref: CS-9208 / CMSADMIN-2756
             * Get cropper image data, convert view_crop ratios.
             * Use croppers actual loaded image natural dimensions
             * rather than the width/height from imgData props
             * for the total w/h in convertToViewCrop math.
             *
             * This is because the preview props width/height may
             * be from a fresh upload which may not reflect
             * the actual loaded preview image dimensions here,
             * causing the math to be off when converting ratios.
             */
            const imageData = cropperRef.current.cropper.getImageData();

            const viewCrop = convertToViewCrop(
              cropData.x,
              cropData.y,
              cropData.width,
              cropData.height,
              imageData.naturalWidth,
              imageData.naturalHeight,
            );

            /**
             * Build updated image preview state.
             *
             * Also update the width/height values, using the
             * natural dimensions of the loaded image, rather than
             * potentially mismatched dimensions from the imgData props.
             *
             * So collaboration clients are able to calculate the same
             * cropped preview via the view_crop and width/height data
             * as the values will be shared over syncstore.
             *
             * Without this, clients may read the `view_crop` relative
             * to the base preview image (x.preview.jpg) dimensions,
             * but on fresh uploads the width/height in imgData may be
             * from the original upload response which can be incorrect
             * if the dimensions differ from the loaded clean preview url dimensions.
             * Leading to incorrect calculations in `getCroppedViaCanvas` / `convertToCropper`.
             *
             */

            const updatedImageState = {
              ...imgData,
              preview: croppedDataURL,
              crop: viewCrop,
              width: imageData.naturalWidth,
              height: imageData.naturalHeight,
            };

            // Return the relevant values
            return {
              croppedDataURL,
              viewCrop,
              updatedImageState,
            };
          }
        }
        // ref not ready / no data
        return null;
      };

      // Cropper requires src
      if (!imgData?.src) {
        return null;
      }

      return (
        <Box
          sx={sx}
          data-testid="image-crop-component"
        >
          <Tooltip
            title={triggerButtonProps?.disabled ? undefined : strCrop}
            placement="top"
            disableInteractive
          >
            <span>
              <Button
                data-testid="image-crop-trigger-button"
                variant={triggerButtonProps?.variant ?? 'outlined'}
                size={triggerButtonProps?.size ?? 'medium'}
                onClick={handleOpen}
                disabled={triggerButtonProps?.disabled}
                fullWidth={triggerButtonProps?.fullWidth}
                endIcon={
                  triggerButtonProps?.label ? <CropIcon fontSize="small" /> : undefined
                }
              >
                {triggerButtonProps?.label ?? <CropIcon fontSize="small" />}
              </Button>
            </span>
          </Tooltip>

          <Dialog
            data-testid="image-crop-dialog-root"
            open={open}
            onClose={handleCancel}
            fullScreen={isFullScreen}
            disableEnforceFocus={true}
            fullWidth={true}
            maxWidth="xl"
            keepMounted={keepCropMounted}
          >
            <DialogContent
              data-testid="image-crop-dialog-content"
              sx={{
                display: 'grid',
                gridTemplateAreas: '"aspectselect" "cropperarea"',
                gridTemplateRows: 'auto 1fr',
                p: 1.5,
                gap: 0.5,
                overflow: 'hidden',
                ...cropperSx,
              }}
            >
              <FormControl
                sx={{
                  gridArea: 'aspectselect',
                  display: 'flex',
                  width: 200,
                  m: 0,
                }}
              >
                <InputLabel
                  id="aspect-ratio-select-label"
                  data-testid="image-crop-aspect-ratio-select-label"
                >
                  {strAspectRatio}
                </InputLabel>
                <Select
                  labelId="aspect-ratio-select-label"
                  id="aspect-ratio-select"
                  data-testid="image-crop-aspect-ratio-select"
                  value={selectedAspectRatio}
                  label="Aspect Ratio"
                  onChange={handleSelectChange}
                  variant="filled"
                >
                  <MenuItem value={0}>Free Form</MenuItem>
                  <MenuItem value={1 / 1}>Square</MenuItem>
                  <MenuItem value={2 / 1}>2:1</MenuItem>
                  <MenuItem value={2 / 3}>2:3</MenuItem>
                  <MenuItem value={4 / 3}>4:3</MenuItem>
                  <MenuItem value={8 / 3}>8:3</MenuItem>
                  <MenuItem value={16 / 9}>16:9</MenuItem>
                  <MenuItem value={40 / 21}>40:21</MenuItem>
                </Select>
              </FormControl>

              <Cropper
                data-testid="image-crop-cropper"
                crossOrigin="anonymous"
                src={imgData.src}
                ready={handleOnReady}
                data={currentCrop}
                autoCropArea={1}
                background={false}
                checkOrientation={false}
                dragMode="move"
                guides={true}
                minCropBoxHeight={50}
                minCropBoxWidth={50}
                movable={false}
                ref={cropperRef}
                responsive={true}
                scalable={false}
                viewMode={2}
                initialAspectRatio={initialAspectRatio}
                aspectRatio={selectedAspectRatio}
                style={{
                  gridArea: 'cropperarea',
                  maxHeight: '80vh',
                  width: '100%',
                  overflow: 'hidden',
                }}
              />
            </DialogContent>

            <DialogActions>
              <Button
                data-testid="image-crop-remove-crop-button"
                disableElevation={true}
                startIcon={<DeleteIcon />}
                onClick={handleRemoveCrop}
              >
                {strRemoveCrop}
              </Button>

              <Box sx={{ flexGrow: 1 }} />
              <Button
                data-testid="image-crop-update-button"
                disableElevation={true}
                variant="contained"
                onClick={handleCrop}
              >
                {strUpdate}
              </Button>
              <Button
                data-testid="image-crop-cancel-button"
                disableElevation={true}
                onClick={handleCancel}
                variant="outlined"
              >
                {strCancel}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      );
    },
  ),
);
ImageCrop.displayName = 'ImageCrop';

/**
 * Cropper.js v1 styles - react-cropper still uses v1 internally
 * The related update to cropperjs v2 removed the css file.
 * Copied over all the styles and converted to mui sx prop
 * to avoid side effect import of global css file.
 *
 * https://github.com/react-cropper/react-cropper/issues/29
 * Ensure one parent of <Cropper> has this sx applied.
 * import 'cropperjs/dist/cropper.css';
 * import 'react-cropper/node_modules/cropperjs/src/css/cropper.css';
 */
const getCropperStyles = (theme: Theme): SxProps<Theme> => {
  const cropperStyles: SxProps<Theme> = {
    '.cropper-container': {
      direction: 'ltr',
      fontSize: 0,
      lineHeight: 0,
      position: 'relative',
      touchAction: 'none',
      WebkitTouchCallout: 'none',
      userSelect: 'none',
      '& img': {
        backfaceVisibility: 'hidden',
        display: 'block',
        height: '100%',
        imageOrientation: '0deg',
        maxHeight: 'none !important',
        maxWidth: 'none !important',
        minHeight: '0 !important',
        minWidth: '0 !important',
        width: '100%',
      },
    },
    '.cropper-wrap-box, .cropper-canvas, .cropper-drag-box, .cropper-crop-box, .cropper-modal':
      {
        bottom: 0,
        left: 0,
        position: 'absolute',
        right: 0,
        top: 0,
      },
    '.cropper-wrap-box, .cropper-canvas': {
      overflow: 'hidden',
    },
    '.cropper-drag-box': {
      backgroundColor: '#fff',
      opacity: 0,
    },
    '.cropper-modal': {
      backgroundColor: '#000',
      opacity: 0.5,
    },
    '.cropper-view-box': {
      display: 'block',
      height: '100%',
      outline: '1px solid #39f',
      outlineColor: 'rgba(51, 153, 255, 0.75)',
      overflow: 'hidden',
      width: '100%',
    },
    '.cropper-dashed': {
      border: '0 dashed #eee',
      display: 'block',
      opacity: 0.5,
      position: 'absolute',
      '&.dashed-h': {
        borderBottomWidth: 1,
        borderTopWidth: 1,
        height: 'calc(100% / 3)',
        left: 0,
        top: 'calc(100% / 3)',
        width: '100%',
      },
      '&.dashed-v': {
        borderLeftWidth: 1,
        borderRightWidth: 1,
        height: '100%',
        left: 'calc(100% / 3)',
        top: 0,
        width: 'calc(100% / 3)',
      },
    },
    '.cropper-center': {
      display: 'block',
      height: 0,
      left: '50%',
      opacity: 0.75,
      position: 'absolute',
      top: '50%',
      width: 0,
      '&::before, &::after': {
        backgroundColor: '#eee',
        content: '" "',
        display: 'block',
        position: 'absolute',
      },
      '&::before': { height: 1, left: -3, top: 0, width: 7 },
      '&::after': { height: 7, left: 0, top: -3, width: 1 },
    },
    '.cropper-face, .cropper-line, .cropper-point': {
      display: 'block',
      height: '100%',
      opacity: 0.1,
      position: 'absolute',
      width: '100%',
    },
    '.cropper-face': {
      backgroundColor: '#fff',
      left: 0,
      top: 0,
    },
    '.cropper-line': {
      backgroundColor: '#39f',
      '&.line-e': { cursor: 'ew-resize', right: -3, top: 0, width: 5 },
      '&.line-n': { cursor: 'ns-resize', height: 5, left: 0, top: -3 },
      '&.line-w': { cursor: 'ew-resize', left: -3, top: 0, width: 5 },
      '&.line-s': { bottom: -3, cursor: 'ns-resize', height: 5, left: 0 },
    },
    '.cropper-point': {
      backgroundColor: '#39f',
      height: 5,
      opacity: 0.75,
      width: 5,
      '&.point-e': { cursor: 'ew-resize', marginTop: -3, right: -3, top: '50%' },
      '&.point-n': { cursor: 'ns-resize', left: '50%', marginLeft: -3, top: -3 },
      '&.point-w': { cursor: 'ew-resize', left: -3, marginTop: -3, top: '50%' },
      '&.point-s': { bottom: -3, cursor: 's-resize', left: '50%', marginLeft: -3 },
      '&.point-ne': { cursor: 'nesw-resize', right: -3, top: -3 },
      '&.point-nw': { cursor: 'nwse-resize', left: -3, top: -3 },
      '&.point-sw': { bottom: -3, cursor: 'nesw-resize', left: -3 },
      '&.point-se': {
        bottom: -3,
        cursor: 'nwse-resize',
        height: 20,
        opacity: 1,
        right: -3,
        width: 20,
        '@media (min-width: 768px)': { height: 15, width: 15 },
        '@media (min-width: 992px)': { height: 10, width: 10 },
        '@media (min-width: 1200px)': { height: 5, opacity: 0.75, width: 5 },
        '&::before': {
          backgroundColor: '#39f',
          bottom: '-50%',
          content: '" "',
          display: 'block',
          height: '200%',
          opacity: 0,
          position: 'absolute',
          right: '-50%',
          width: '200%',
        },
      },
    },
    '.cropper-invisible': { opacity: 0 },
    '.cropper-bg': {
      // note: original used a checkerboard png (via css url)
      backgroundColor: '#ccc',
    },
    '.cropper-hide': { display: 'block', height: 0, position: 'absolute', width: 0 },
    '.cropper-hidden': { display: 'none !important' },
    '.cropper-move': { cursor: 'move' },
    '.cropper-crop': { cursor: 'crosshair' },
    '.cropper-disabled .cropper-drag-box, .cropper-disabled .cropper-face, .cropper-disabled .cropper-line, .cropper-disabled .cropper-point':
      {
        cursor: 'not-allowed',
      },
  };

  return cropperStyles;
};
